<template>
    <div
        class="EntitySearchSelection"
        :class="{
            'EntitySearchSelection--disabled': isDisabled,
            'EntitySearchSelection--error': hasError,
        }"
    >
        <div class="EntitySearchSelection__label">
            {{ label }}
        </div>
        <div class="EntitySearchSelection__search">
            <EidIcon class="mr-2" :icon-src="faMagnifyingGlass" />
            <input
                class="EntitySearchSelection__searchInput"
                type="text"
                :placeholder="placeholder"
                :disabled="isDisabled"
                @input="onSearchInput"
            >
        </div>
        <div
            ref="resultsContainer"
            class="EntitySearchSelection__results"
        >
            <div
                v-if="entities.length === 0 && !loading && finished"
                class="flex items-center justify-center h-full"
            >
                No entities found
            </div>
            <template v-else>
                <div v-if="entities.length > 0" class="EntitySearchSelection__list">
                    <div
                        v-for="entity in entities"
                        :key="entity.id"
                        class="EntitySearchSelection__item"
                        :class="{
                            'EntitySearchSelection__item--selected': selectedEntityId === entity.id,
                        }"
                        @click="!isDisabled && selectEntity(entity)"
                    >
                        <div class="EntitySearchSelection__itemName">
                            {{ entity.displayName }}
                        </div>
                        <div class="EntitySearchSelection__itemEmail">
                            {{ entity.contactEmail }}
                        </div>
                        <div
                            v-if="showAddress && formatAddress(entity)"
                            class="EntitySearchSelection__itemAddress"
                        >
                            <EidIcon class="mr-1" :icon-src="faMapMarkerAlt" />
                            {{ formatAddress(entity) }}
                        </div>
                    </div>
                </div>
                <div v-if="loading" class="EntitySearchSelection__loader h-full">
                    <LineLoader :dot-amount="3" />
                </div>
                <div v-else-if="finished && entities.length > 0" class="EntitySearchSelection__status">
                    All entities displayed.
                </div>
            </template>
        </div>
        <div
            v-if="selectedEntity"
            class="EntitySearchSelection__selectedCard"
        >
            <div class="EntitySearchSelection__selectedCardContent">
                <div class="EntitySearchSelection__selectedCardIndicator" />
                <div class="EntitySearchSelection__selectedCardInfo">
                    {{ selectedEntity.displayName }}
                </div>
            </div>
            <NewButton
                v-if="!isDisabled"
                variant="flat"
                color="neutral"
                size="icon-sm"
                class="EntitySearchSelection__selectedCardRemove"
                @click="deselectEntity"
            >
                <EidIcon :icon-src="faXmark" />
            </NewButton>
        </div>
        <p v-if="instruction" class="EntitySearchSelection__instruction">
            {{ instruction }}
        </p>
    </div>
</template>

<style lang="scss">
.EntitySearchSelection {
    background-color: var(--eidBgGrayTones-lightest);
    border: 1px solid var(--eidTechGray-25);
    border-radius: var(--primary-border-radius);
    padding: 16px;
    display: flex;
    flex-direction: column;

    &--disabled {
        .EntitySearchSelection__search,
        .EntitySearchSelection__results,
        .EntitySearchSelection__selectedCard {
            opacity: 0.6;
            cursor: not-allowed;
        }
    }

    &--error {
        border-color: var(--eidDangerRed);
    }

    &__label {
        font-weight: 500;
        font-size: 14px;
        margin-bottom: 8px;
    }

    &__search {
        display: flex;
        align-items: center;
        padding: 8px 12px;
        margin-bottom: 12px;
        background: var(--eidWhite);
        border: 1px solid var(--eidTechGray-25);
        border-radius: 4px;
        color: var(--eidSecureBlue-75);

        &--disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }
    }

    &__searchInput {
        flex: 1;
        border: none;
        outline: none;
        font-size: 14px;

        &::placeholder {
            color: var(--eidSecureBlue-75);
        }

        &:disabled {
            cursor: not-allowed;
            background: transparent;
        }
    }

    &__results {
        flex: 1;
        position: relative;
        overflow-y: auto;
        background: var(--eidWhite);
        border: 1px solid var(--eidTechGray-25);
        border-radius: 4px;
    }

    &__item {
        padding: 6px 16px;
        cursor: pointer;

        &:not(&--selected):hover {
            background-color: var(--eidTechGray-10);
        }

        &--selected {
            background-color: var(--eidTrustGreen-10);

            .EntitySearchSelection__itemName {
                font-weight: 500;
                color: var(--eidTrustGreen);
            }
        }
    }

    &__itemName {
        font-size: 14px;
        color: var(--eidSecureBlue);
    }

    &__itemEmail {
        font-size: 12px;
        color: var(--eidSecureBlue-75);
    }

    &__itemAddress {
        display: flex;
        align-items: center;
        font-size: 12px;
        color: var(--eidSecureBlue-75);
    }

    &__loader,
    &__status {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        font-size: 12px;
        padding: 12px;
        border-top: 1px solid var(--eidTechGray-10);
    }

    &__selectedCard {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-top: 12px;
        padding: 8px 10px 8px 12px;
        background: var(--eidWhite);
        border: 1px solid var(--eidTechGray-25);
        border-radius: 4px;
    }

    &__selectedCardIndicator {
        width: 8px;
        height: 8px;
        margin-right: 8px;
        flex-shrink: 0;
        border-radius: 50%;
        background-color: var(--eidTrustGreen);
    }

    &__selectedCardContent {
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        font-weight: 500;
        color: var(--eidDarkBlue);
    }

    &__selectedCardRemove {
        flex-shrink: 0;
    }

    &__instruction {
        color: var(--eidQualityBlack-75);
        font-size: 14px;
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import throttle from 'lodash/throttle';

    import { faXmark, faMagnifyingGlass, faMapMarkerAlt } from '@fortawesome/pro-light-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import LineLoader from '@evidentid/dashboard-commons/components/LineLoader/LineLoader.vue';
    import { Entity } from '@evidentid/tprm-portal-lib/models/dashboard/Entity.model';

    import NewButton from '@/components/new-button/NewButton.vue';
    import { DashboardState, useDashboardStore } from '@/modules/dashboard/vuex/dashboard';

    const SCROLL_END_THRESHOLD = 85;
    const SEARCH_DEBOUNCE_MS = 500;

    export default defineComponent({
        name: 'EntitySearchSelection',
        components: {
            EidIcon,
            LineLoader,
            NewButton,
        },
        props: {
            selectedEntityId: {
                type: String as PropType<string | null>,
                default: null,
            },
            isDisabled: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            label: {
                type: String,
                default: 'Select entity',
            },
            placeholder: {
                type: String,
                default: 'Search entities...',
            },
            showAddress: {
                type: Boolean,
                default: false,
            },
            instruction: {
                type: String,
                default: '',
            },
            hasError: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        emits: [ 'select' ],
        data() {
            return {
                faMagnifyingGlass,
                faXmark,
                faMapMarkerAlt,
                searchQuery: '',
                debounceTimeout: undefined as number | undefined,
                selectedEntity: null as Entity | null,
                hasInitialLoad: false,
            };
        },
        computed: {
            rpName(): string {
                return (this as any).$rp?.current || '';
            },
            entitiesStatus() {
                return (useDashboardStore().$state as DashboardState).entities[(this as any).rpName] || {
                    loading: false,
                    finished: false,
                    list: [],
                };
            },
            entities(): Entity[] {
                return (this as any).entitiesStatus.list || [];
            },
            loading(): boolean {
                return (this as any).entitiesStatus.loading || false;
            },
            finished(): boolean {
                return (this as any).entitiesStatus.finished || false;
            },
        },
        watch: {
            selectedEntityId: {
                immediate: true,
                async handler() {
                    await (this as any).updateSelectedEntity();
                },
            },
            entities: {
                async handler() {
                    await (this as any).updateSelectedEntity();
                },
            },
            isDisabled(newVal: boolean, oldVal: boolean) {
                // while this comp loads entity on mount, but if isDisabled is async, it might be true when mounted
                // so we need to also watch when isDisabled becomes false and perform initial load.
                if (oldVal && !newVal && !(this as any).hasInitialLoad) {
                    (this as any).performInitialLoad();
                }
            },
        },
        mounted() {
            if (!this.isDisabled) {
                (this as any).performInitialLoad();
            }
        },
        beforeUnmount() {
            (this as any).cleanup();
            (this as any).clearEntitiesList();
        },
        methods: {
            formatAddress(entity: Entity): string {
                const { address } = entity;
                if (!address) {
                    return '';
                }
                return [ address.street, address.city, address.state ].filter(Boolean).join(', ');
            },
            onSearchInput(event: InputEvent): void {
                if (this.isDisabled) {
                    return;
                }
                (this as any).searchQuery = (event.target as HTMLInputElement).value;
                (this as any).debounceSearch();
            },
            debounceSearch(): void {
                if ((this as any).debounceTimeout) {
                    clearTimeout((this as any).debounceTimeout);
                }
                (this as any).debounceTimeout = setTimeout(() => {
                    (this as any).reloadEntities();
                }, SEARCH_DEBOUNCE_MS) as unknown as number;
            },
            setupScrollListener(): void {
                const container = this.$refs.resultsContainer as HTMLElement;
                if (container) {
                    const scrollListener = throttle((this as any).handleScroll, 100);
                    container.addEventListener('scroll', scrollListener);
                    (this as any).scrollListener = scrollListener;
                }
            },
            handleScroll(): void {
                if (this.isDisabled) {
                    return;
                }

                const container = this.$refs.resultsContainer as HTMLElement;
                if (!container) {
                    return;
                }

                const { scrollTop, scrollHeight, clientHeight } = container;
                const scrollPercentage = ((scrollTop + clientHeight) / scrollHeight) * 100;

                if (scrollPercentage >= SCROLL_END_THRESHOLD) {
                    (this as any).loadNextEntities();
                }
            },
            performInitialLoad(): void {
                if ((this as any).hasInitialLoad) {
                    return;
                }
                (this as any).hasInitialLoad = true;
                (this as any).loadEntities();
                (this as any).setupScrollListener();
            },
            async reloadEntities(): Promise<void> {
                (this as any).clearEntitiesList();
                await (this as any).loadEntities();
            },
            async loadEntities(): Promise<void> {
                const filters = (this as any).searchQuery
                    ? { search: (this as any).searchQuery, sort: 'displayName' }
                    : { sort: 'displayName' };

                await useDashboardStore().loadEntities({
                    rpName: (this as any).rpName,
                    filters,
                });
            },
            async loadNextEntities(): Promise<void> {
                if ((this as any).loading || (this as any).finished) {
                    return;
                }
                const filters = (this as any).searchQuery
                    ? { search: (this as any).searchQuery, sort: 'displayName' }
                    : { sort: 'displayName' };

                await useDashboardStore().loadNextEntities({
                    rpName: (this as any).rpName,
                    filters,
                });
            },
            selectEntity(entity: Entity): void {
                (this as any).selectedEntity = entity;
                this.$emit('select', entity.id);
            },
            deselectEntity(): void {
                (this as any).selectedEntity = null;
                this.$emit('select', null);
            },
            async updateSelectedEntity(): Promise<void> {
                if (!this.selectedEntityId) {
                    (this as any).selectedEntity = null;
                    return;
                }

                // Try to find the entity in the current list
                const foundEntity = (this as any).entities.find(
                    (entity: Entity) => entity.id === this.selectedEntityId,
                );
                if (foundEntity) {
                    (this as any).selectedEntity = foundEntity;
                    return;
                }
                // If not found in current list (e.g., due to search filter), keep the existing selectedEntity
                // This preserves the selection even when the entity is not in the filtered results
                // however if selectedEntity is also null,
                // then it's either on initial load the selected entity is not loaded within first batch,
                // or the selected entity is no longer exist after all entities are loaded.
                // in this case, we need to create Entity with id only for display purposes and try to load it with id
                if (!foundEntity && !(this as any).selectedEntity) {
                    (this as any).selectedEntity = {
                        id: this.selectedEntityId,
                        displayName: `Id: ${this.selectedEntityId}`,
                    } as Entity;
                    await (this as any).loadSingleEntity(this.selectedEntityId);
                }
            },
            async loadSingleEntity(id: string): Promise<void> {
                await useDashboardStore().loadEntity({
                    rpName: (this as any).rpName,
                    id,
                });
                // After loading, check if the entity is now in the list and update selectedEntity
                const loadedEntity = (this as any).entities.find(
                    (entity: Entity) => entity.id === id,
                );
                if (loadedEntity) {
                    (this as any).selectedEntity = loadedEntity;
                }
            },
            clearEntitiesList(): void {
                if ((this as any).rpName) {
                    useDashboardStore().clearEntitiesList({ rpName: (this as any).rpName });
                }
            },
            cleanup(): void {
                if ((this as any).debounceTimeout) {
                    clearTimeout((this as any).debounceTimeout);
                }

                const container = this.$refs.resultsContainer as HTMLElement;
                const scrollListener = (this as any).scrollListener;
                if (container && scrollListener) {
                    container.removeEventListener('scroll', scrollListener);
                    scrollListener.cancel();
                }
            },
        },
    });
</script>
